import { FormProvider, useForm, useFormContext } from "react-hook-form";
import { DateTime } from "luxon";
import { Student } from "../../providers/studentsProvider";
import { DrivingLessonDurationInput } from "../../inputs/DrivingLessonDurationInput";
import { DrivingLicenseClassInput } from "../../inputs/DrivingLicenseClassInput";
import { DateTimeInput } from "../../inputs/DateTimeInput";
import { StudentInputField } from "./StudentInputField";
import { DrivingLicenseClass } from "../../model/DrivingLicenseClass";
import { Column } from "../../misc/Column";
import { Row } from "../../misc/Row";
import { PriceSuggestion } from "./PriceSuggestion";
import { BookCalendarEventWithoutInviteButton } from "../../buttons/BookCalendarEventWithoutInviteButton";
import { grants } from "../../backoffice.access_control";
import { type Simulator } from "../../providers/vehiclesProvider";
import { Form2Theme } from "../../misc/Form2";
import { serverAPI } from "../../api/server.api";
import { v4 as randomUuid } from "uuid";
import { handleBookingError } from "../../utils/handleBookingError";
import { LocalizedError } from "../../utils/LocalizedError";
import { SelectInput } from "../../inputs/SelectInput";
import { SaveButton2 } from "../../buttons/SaveButton2";
import { SimulatorOption, useSimulatorOptions } from "./useSimulatorOptions";
import { useCurrentDrivingSchoolId } from "../../hooks/useCurrentDrivingSchoolId";

export interface CreateSimulatorDrivingLessonFormValues {
  dateTime?: DateTime;
  drivingLessonType: "simulator";
  durationInMinutes: number;
  student?: Student;
  instructorId?: never;
  drivingLicenseClass: DrivingLicenseClass | "";
  simulatorId?: string;
}

export interface BookSimulatorDrivingLessonParams {
  dateTime: DateTime;
  duration: { minutes: number };
  instructorId?: never;
  studentId: string;
  bookedTrainingId: string;
  simulatorId: string;
}

export const SIMULATOR_DRIVING_LESSON_DURATION_OPTIONS = [45, 90, 135, 180];

export function CreateSimulatorDrivingLessonForm(props: {
  simulator?: Simulator;
  student?: Student;
  initialDateTime?: DateTime;
  onSuccess: () => void;
}) {
  const { initialDateTime } = props;
  const defaultValues = {
    drivingLessonType: "simulator",
    dateTime: initialDateTime,
    durationInMinutes: 45,
    student: props.student,
    simulatorId: props.simulator?.id ?? "",
    drivingLicenseClass: (() => {
      const { student } = props;
      if (!student) {
        return "";
      }
      const { activeOrMostRecentBookedTraining } = student;
      if (activeOrMostRecentBookedTraining.includesSimulatorDrivingLessons) {
        return activeOrMostRecentBookedTraining.drivingLicenseClass;
      }
      const relevantBookedTrainings = student.bookedTrainings.filter(
        (it) => it.includesSimulatorDrivingLessons && !it.isFinished,
      );
      return relevantBookedTrainings.length > 0 ? relevantBookedTrainings[0].drivingLicenseClass : "";
    })(),
  } satisfies CreateSimulatorDrivingLessonFormValues;
  const formProps = useForm<CreateSimulatorDrivingLessonFormValues>({ defaultValues });
  return (
    <Form2Theme>
      <FormProvider {...formProps}>
        <_SimulatorDrivingLessonForm {...props} />
      </FormProvider>
    </Form2Theme>
  );
}

function _SimulatorDrivingLessonForm(props: {
  simulator?: Simulator;
  student?: Student;
  initialDateTime?: DateTime;
  onSuccess: () => void;
}) {
  const { onSuccess } = props;
  const drivingSchoolId = useCurrentDrivingSchoolId();
  const { handleSubmit, watch } = useFormContext<CreateSimulatorDrivingLessonFormValues>();
  const { simulatorOptions, getSettledSimulatorOptions } = useSimulatorOptions(props.simulator?.id);

  function validateWithoutSimulator(
    formValues: CreateSimulatorDrivingLessonFormValues,
  ): Omit<BookSimulatorDrivingLessonParams, "simulatorId"> {
    const { dateTime, durationInMinutes, student, drivingLicenseClass } = formValues;
    if (!dateTime) {
      throw new Error(`Invalid dateTime: ${dateTime}`);
    }
    if (!durationInMinutes) {
      throw new Error(`Invalid durationInMinutes: ${durationInMinutes}`);
    }
    if (!student) {
      throw new Error(`Invalid student: ${student}`);
    }
    if (!drivingLicenseClass) {
      throw new Error(`Invalid drivingLicenseClass: ${drivingLicenseClass}`);
    }
    const bookedTraining = student.bookedTrainings.find((it) => it.drivingLicenseClass === drivingLicenseClass);
    if (!bookedTraining) {
      throw new Error(`Student ${student.id} has no booked training for driving license class ${drivingLicenseClass}`);
    }
    return {
      dateTime,
      duration: { minutes: durationInMinutes },
      studentId: student.id,
      bookedTrainingId: bookedTraining.id,
    };
  }

  async function validate(
    formValues: CreateSimulatorDrivingLessonFormValues,
  ): Promise<BookSimulatorDrivingLessonParams> {
    const payloadWithoutSimulator = validateWithoutSimulator(formValues);
    const { simulatorId } = formValues;
    if (!simulatorId) {
      throw new Error(`Invalid simulatorId: ${simulatorId}`);
    } else {
      const settledSimulatorOptions = await getSettledSimulatorOptions();
      const option = settledSimulatorOptions.find((it) => it[0] === simulatorId);
      if (!option) {
        throw new Error(`Invalid simulatorId: ${simulatorId} -- not in simulatorOptions`);
      }
      if (option.hasTimeConflict) {
        throw new LocalizedError(`${option.simulatorName} ist zur angegebenen Uhrzeit nicht verfügbar.`);
      }
    }
    return {
      ...payloadWithoutSimulator,
      simulatorId,
    } satisfies BookSimulatorDrivingLessonParams;
  }

  const onSubmit =
    ({ studentRsvp }: { studentRsvp: "pending" | "accepted" }) =>
    async (formValues: CreateSimulatorDrivingLessonFormValues) => {
      try {
        const { dateTime, duration, studentId, bookedTrainingId, simulatorId } = await validate(formValues);
        const isInThePast = dateTime < DateTime.now();
        const simulator =
          props.simulator ?? (await dataProvider.getOne<Simulator>("simulators", { id: simulatorId })).data;
        const branch = (await dataProvider.getOne("branches", { id: simulator.branchId })).data;
        await serverAPI.createCalendarEvent({
          id: randomUuid(),
          type: "DrivingLesson",
          drivingLessonType: "simulator",
          start: dateTime.toISO(),
          end: dateTime.plus(duration).toISO(),
          student: { id: studentId, bookedTrainingId, rsvp: studentRsvp },
          vehicleId: simulatorId,
          startLocation: branch.address,
        });
        showSuccessToast(
          studentRsvp === "accepted"
            ? "Simulator-Fahrstunde erfolgreich gebucht."
            : isInThePast
              ? "Simulator-Fahrstunde erfolgreich nachgetragen."
              : "Einladung erfolgreich versandt.",
        );
        onSuccess();
      } catch (error) {
        if (!(await handleBookingError(error, student))) {
          const errorMessage =
            studentRsvp === "pending"
              ? "Fehler beim Versenden der Einladung"
              : formValues.dateTime && formValues.dateTime < DateTime.now()
                ? "Fehler beim Nachtragen der Simulator-Fahrstunde"
                : "Fehler beim Buchen der Simulator-Fahrstunde";
          handleError(errorMessage, error);
        }
      }
    };

  const { student, dateTime } = watch();

  return (
    <form onSubmit={handleSubmit(onSubmit({ studentRsvp: "pending" }))} style={{ height: "100%" }}>
      <Column height="100%" minHeight={500} gap="1em">
        <DateTimeInput source="dateTime" size="small" allowPastDates />
        <DrivingLessonDurationInput
          source="durationInMinutes"
          options={SIMULATOR_DRIVING_LESSON_DURATION_OPTIONS}
          size="small"
        />
        {!props.student && (
          <StudentInputField
            drivingSchoolId={drivingSchoolId}
            filterStudents={_canBookSimulatorDrivingLessons}
            size="small"
            disabled={!!props.student}
          />
        )}
        {student && (
          <DrivingLicenseClassInput
            student={student}
            source="drivingLicenseClass"
            label="Ausbildung für Führerscheinklasse"
            size="small"
            includesSimulatorDrivingLessons
          />
        )}
        {!props.simulator && <_SimulatorInputField options={simulatorOptions} />}
        <PriceSuggestion validate={validateWithoutSimulator} />
        <Row sx={{ justifyContent: "flex-end", mt: "auto", gap: 1 }}>
          {grants.has("DrivingLesson:bookWithoutInvite") && (
            <BookCalendarEventWithoutInviteButton
              eventType="drivingLesson"
              onSubmit={handleSubmit(onSubmit({ studentRsvp: "accepted" }))}
            />
          )}
          <SaveButton2 label={dateTime && dateTime < DateTime.now() ? "Termin nachtragen" : "Einladung senden"} />
        </Row>
      </Column>
    </form>
  );
}

function _SimulatorInputField(props: { options: undefined | Array<SimulatorOption>; disabled?: boolean }) {
  return (
    <SelectInput
      source="simulatorId"
      label="Simulator"
      options={props.options}
      validate={(value) => (value ? true : "Bitte wähle einen Simulator aus.")}
      size="small"
      disabled={props.disabled}
    />
  );
}

function _canBookSimulatorDrivingLessons(student: Student) {
  return student.bookedTrainings.some((it) => it.includesSimulatorDrivingLessons);
}
